import React, { useEffect, useRef, useState } from 'react';
import { ScrollText } from 'lucide-react';
import { useDevToolsStore } from '../../store/devToolsStore';
import { useUiModalStore, setAiPanelOpen } from '../../store/uiModalStore';
import { getAppVersion } from '../../services/updateService';
import { isWebApp } from '../../platform/target';
import Ghost from '../auth/Ghost.jsx';
import GitStatusBarItem from '../git/GitStatusBarItem';

// Short cute one-liners the footer ghost occasionally blurts out.
const GHOST_QUOTES = [
  'boo!',
  'need a hand?',
  'ask me anything',
  'psst… try the AI panel',
  'stuck? poke me',
  'i live down here',
  'ship it',
  '200 OK vibes',
  'nice request!',
  'coffee break?',
  'you got this',
  'hmm, spooky bug?',
];

export default function DevToolsFooter() {
  const isOpen = useDevToolsStore((s) => s.isOpen);
  const activeTab = useDevToolsStore((s) => s.activeTab);
  const toggle = useDevToolsStore((s) => s.toggle);
  const setActiveTab = useDevToolsStore((s) => s.setActiveTab);
  const closeDevTools = useDevToolsStore((s) => s.close);

  const aiPanelOpen = useUiModalStore((s) => s.aiPanelOpen);
  // The Console tab only makes sense alongside request traffic — restrict it to
  // the Collections and Request History panels. In other panels (Task Manager,
  // DB Manager, Teams, Admin) the button is hidden.
  const appPanel = useUiModalStore((s) => s.activeTab);
  const showConsole = appPanel === 'collections' || appPanel === 'history';

  // If we navigate to a panel where Console is hidden while the Console panel is
  // open, close it so it can't linger with no button to toggle it back.
  useEffect(() => {
    if (!showConsole && isOpen && activeTab === 'console') {
      closeDevTools();
    }
  }, [showConsole, isOpen, activeTab, closeDevTools]);

  // Performance and Terminal left the footer (owner, 07.10.2026: "no need";
  // the Terminal is on the rail). A Performance panel still open from before
  // would have no button left to close it.
  useEffect(() => {
    if (isOpen && activeTab === 'performance') closeDevTools();
  }, [isOpen, activeTab, closeDevTools]);

  const [appVersion, setAppVersion] = useState('');
  const [quote, setQuote] = useState(null);

  // Keep the latest aiPanelOpen readable inside the self-scheduling loop
  // without re-arming the timers each time it toggles.
  const aiPanelOpenRef = useRef(aiPanelOpen);
  aiPanelOpenRef.current = aiPanelOpen;

  useEffect(() => {
    let cancelled = false;
    getAppVersion().then((v) => { if (!cancelled) setAppVersion(v || ''); });
    return () => { cancelled = true; };
  }, []);

  // Randomly pop a short quote, then hide it — a gentle, irregular cadence.
  // Respect reduced-motion and stay quiet while the AI panel is already open.
  useEffect(() => {
    if (typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }
    let showTimer;
    let hideTimer;
    let lastIdx = -1;
    const scheduleNext = () => {
      // 18–45s between pops
      showTimer = setTimeout(() => {
        if (!aiPanelOpenRef.current) {
          let idx = Math.floor(Math.random() * GHOST_QUOTES.length);
          if (idx === lastIdx) idx = (idx + 1) % GHOST_QUOTES.length;
          lastIdx = idx;
          setQuote(GHOST_QUOTES[idx]);
          hideTimer = setTimeout(() => setQuote(null), 4200);
        }
        scheduleNext();
      }, 18000 + Math.random() * 27000);
    };
    // First pop lands a little sooner so it feels alive on load.
    showTimer = setTimeout(() => {
      if (!aiPanelOpenRef.current) {
        const idx = Math.floor(Math.random() * GHOST_QUOTES.length);
        lastIdx = idx;
        setQuote(GHOST_QUOTES[idx]);
        hideTimer = setTimeout(() => setQuote(null), 4200);
      }
      scheduleNext();
    }, 6000 + Math.random() * 6000);
    return () => { clearTimeout(showTimer); clearTimeout(hideTimer); };
  }, []);

  // Hide any bubble the moment the panel opens.
  useEffect(() => { if (aiPanelOpen) setQuote(null); }, [aiPanelOpen]);

  return (
    <div className="devtools-shell flex-shrink-0">
      <div className="devtools-footer">
        <div className="devtools-footer__tabs">
        {/* Git sync status sits fully left (owner, 07.10.2026). */}
        {!isWebApp() && <GitStatusBarItem />}
        {showConsole && (
        <button
          type="button"
          className={`devtools-footer__tab ${activeTab === 'console' && isOpen ? 'active' : ''}`}
          onClick={() => (isOpen && activeTab === 'console' ? toggle() : setActiveTab('console'))}
        >
          <ScrollText size={13} />
          <span>Console</span>
        </button>
        )}
      </div>

      <div className="devtools-footer__actions">
        {appVersion && (
          <span className="devtools-footer__version">v{appVersion}</span>
        )}
        <div className="devtools-footer__ghost-wrap">
          {quote && (
            <span key={quote} className="devtools-footer__ghost-bubble" role="status">
              {quote}
            </span>
          )}
          <button
            type="button"
            className={`devtools-footer__ghost ${aiPanelOpen ? 'active' : ''}`}
            onClick={() => setAiPanelOpen(!aiPanelOpen)}
            title={aiPanelOpen ? 'Close AI panel' : 'Open AI panel'}
            aria-label={aiPanelOpen ? 'Close AI panel' : 'Open AI panel'}
            aria-pressed={aiPanelOpen}
          >
            <Ghost variant="classic" size={16} animated />
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}
